import { useEffect, useState } from "react";

import { Message } from "@twilio/conversations";
import { HelpCenterApi } from "@twiliointernal/org/twilio/e2-commons/shared/api";
import { LiveChatProps } from "@twiliointernal/org/twilio/e2-commons/shared/definitions";
import {
  AgentEvent,
  ConversationServiceReadyEvent,
  ConversationMetadata,
} from "../../services/conversations/definitions/types";
import {
  AgentActions,
  ConversationEvents,
} from "../../services/conversations/definitions/constants";
import chatService from "../../services/conversations/conversationsService";

const useConversationSetup = ({
  liveChatId,
  authToken,
  identity,
}: LiveChatProps) => {
  const [agentEvents, setAgentEvents] = useState<AgentEvent[]>([]);
  const [currentAgent, setCurrentAgent] = useState<AgentEvent["agent"] | null>(
    null
  );
  const [conversationMetadata, setConversationMetadata] =
    useState<ConversationMetadata | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [sdkError, setSdkError] = useState("");

  useEffect(() => {
    if (!chatService.isServiceReady) {
      chatService.setRefeshTokenCallback(requestRefreshToken);
      chatService.addEventListener<string>(
        ConversationEvents.Error,
        setSdkError
      );
      chatService.addEventListener<boolean>(
        ConversationEvents.Typing,
        setIsTyping
      );
      chatService.addEventListener<Message[]>(
        ConversationEvents.Messages,
        setMessages
      );
      chatService.addEventListener<ConversationServiceReadyEvent>(
        ConversationEvents.Ready,
        handleServiceReady
      );
      chatService.addEventListener<AgentEvent>(
        ConversationEvents.Agent,
        handleAgentEvent
      );
      chatService.initService({
        authToken,
        liveChatId,
        identity,
      });

      return () => {
        removeListeners();
        setConversationMetadata(null);
      };
    }
  }, []);

  const removeListeners = () => {
    chatService.removeEventListener<string>(
      ConversationEvents.Error,
      setSdkError
    );
    chatService.removeEventListener<boolean>(
      ConversationEvents.Typing,
      setIsTyping
    );
    chatService.removeEventListener<Message[]>(
      ConversationEvents.Messages,
      setMessages
    );
    chatService.removeEventListener<ConversationServiceReadyEvent>(
      ConversationEvents.Ready,
      handleServiceReady
    );
    chatService.removeEventListener<AgentEvent>(
      ConversationEvents.Agent,
      handleAgentEvent
    );
    chatService.isServiceReady = false;
    chatService.conversationMetadata = undefined;
  };

  // Callback for when the conversation service is ready
  const handleServiceReady = (event: ConversationServiceReadyEvent) => {
    setIsReady(event.isServiceReady);
    setConversationMetadata(event.conversationMetadata);
  };

  // Callback to receive the name of the agent joined and pass to the respective component
  const handleAgentEvent = (event: AgentEvent) => {
    setAgentEvents([...agentEvents, event]);

    if (event.action === AgentActions.Join) {
      setCurrentAgent(event.agent);
    } else {
      // Notification on Agent Left
      setCurrentAgent(null);
    }
  };

  // Connect with the API to get a new token
  const requestRefreshToken = async (identity: string) => {
    const api = new HelpCenterApi();
    const response = await api.refreshToken({ identity });
    return response.data.token;
  };

  return {
    agentEvents,
    conversationMetadata,
    currentAgent,
    messages,
    sdkError,
    isTyping,
    isReady,
  };
};

export default useConversationSetup;
